import { DONE_STATUS } from '../data/types'
import { cn } from '../lib/cn'
import { motion } from 'framer-motion'
import {
  ArrowRight,
  CalendarClock,
  CheckCircle2,
  Circle,
  Clock3,
  ClipboardList,
  FolderKanban,
  Plus,
  Sparkles,
  Target,
  TrendingUp,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Avatar, AvatarStack } from '../components/Avatar'
import { BarChartMini, DonutChart } from '../components/charts/lazy'
import { BentoCard } from '../components/BentoCard'
import { Chasing } from '../components/Chasing'
import { Button } from '../components/Button'
import { PageHeader } from '../components/PageHeader'
import { ProgressBar } from '../components/ProgressBar'
import { StatCard } from '../components/StatCard'
import { StatusBadge } from '../components/StatusBadge'
import { TimeLeaderboard } from '../components/TimeLeaderboard'
import { useAppData } from '../context/AppDataContext'
import { useAuth } from '../context/AuthContext'
import { useTickDone } from '../hooks/useTickDone'
import { useScopedWork } from '../hooks/useScopedWork'
import { daysUntil, formatDate } from '../lib/format'
import { NewTaskModal } from './board/NewTaskModal'

function useTimeOfDayGreeting() {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 18) return 'Good afternoon'
  return 'Good evening'
}

export default function Dashboard() {
  // `activityLog` arrives from the server already scoped to this account's
  // role/linked persona (see server/src/routes/data.ts:
  // GET /api/data/activity-log) — no client-side filtering needed or
  // trusted here.
  const { directory, activityLog, weeklyTime, history } = useAppData()
  const tick = useTickDone()
  const { tasks, deliverables, projects, clients, content } = useScopedWork()
  const { currentUser } = useAuth()
  const [newTaskOpen, setNewTaskOpen] = useState(false)
  const greeting = useTimeOfDayGreeting()
  const firstName = currentUser?.name?.split(' ')[0] ?? 'there'

  const stats = useMemo(() => {
    const activeProjects = projects.filter((p) => p.status === 'Active').length
    const completedProjects = projects.filter((p) => p.status === 'Completed').length
    const pendingTasks = tasks.filter((t) => t.status !== DONE_STATUS).length
    const completedTasks = tasks.filter((t) => t.status === DONE_STATUS).length
    const deliverablesDue = deliverables.filter((d) => d.status !== 'Completed').length
    return { totalProjects: projects.length, activeProjects, completedProjects, pendingTasks, completedTasks, deliverablesDue }
  }, [tasks, deliverables, projects])

  const taskStatusBreakdown = useMemo(() => {
    const order: { key: string; color: string }[] = [
      { key: 'Briefed', color: '#b8b8c0' },
      { key: 'Editing', color: '#3b7dde' },
      { key: 'Shot', color: '#d98e1c' },
      { key: 'Review', color: '#FC2265' },
      { key: DONE_STATUS, color: '#2f9e57' },
    ]
    return order.map((o) => ({ name: o.key, value: tasks.filter((t) => t.status === o.key).length, color: o.color }))
  }, [tasks])

  const upcoming = useMemo(
    () =>
      [...tasks]
        .filter((t) => t.status !== DONE_STATUS)
        .sort((a, b) => daysUntil(a.dueDate) - daysUntil(b.dueDate))
        .slice(0, 5),
    [tasks],
  )

  const recentTasks = useMemo(() => [...tasks].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)).slice(0, 6), [tasks])

  // Posted per month, from `GET /history`: a piece counts in the month it
  // was owed for, as the monthly report counts it.
  const monthlyDeliverables = useMemo(
    () =>
      history.map((h) => ({
        label: new Date(`${h.month}T00:00:00`).toLocaleString('en', { month: 'short' }),
        value: h.posted,
      })),
    [history],
  )
  const deliveredThisHalf = history.reduce((n, h) => n + h.posted, 0)

  // This month's content plan: everything on it, what is ready to go out
  // (scheduled or already up), and what is posted.
  const contentStats = useMemo(() => {
    const planned = content.length
    const posted = content.filter((c) => c.status === DONE_STATUS).length
    const completed = content.filter((c) => c.status === DONE_STATUS || c.status === 'Scheduled').length
    return { planned, completed, posted }
  }, [content])

  // The aurora canvas is global and theme-aware (see AppShell.tsx) — this page
  // just needs `text-canvas-fg` so its own text/header flips with it.
  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        variant="dark"
        title={`${greeting}, ${firstName}`}
        description="What needs a decision, and what is quietly going wrong."
        actions={
            <Button onClick={() => setNewTaskOpen(true)}>
              <Plus size={16} /> Add a piece
            </Button>
          }
        />

        {/*
          * No `change` prop on any of these, and that is the point.
          *
          * The design shipped them with `change={8}`, `change={-3}` and so on
          * — hardcoded, because it was a demo over mock data. Rendered here
          * they would be six green and red arrows telling a partner that
          * delivery is up 8% on last month, which nothing in this platform has
          * computed and no one has checked. A made-up number that looks
          * measured is worse than no number, because it gets repeated.
          *
          * `StatCard` already draws nothing when `change` is absent. When
          * there is a real month-on-month figure to show, it goes here.
          */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <StatCard label="Clients this month" value={stats.totalProjects} icon={FolderKanban} accent="#FC2265" index={0} />
        <StatCard label="Cycles open" value={stats.activeProjects} icon={Target} accent="#3b7dde" index={1} />
        <StatCard label="Cycles closed" value={stats.completedProjects} icon={CheckCircle2} accent="#2f9e57" index={2} />
        <StatCard label="Pieces in flight" value={stats.pendingTasks} icon={ClipboardList} accent="#d98e1c" index={3} />
        <StatCard label="Posted" value={stats.completedTasks} icon={Sparkles} accent="#8a5cf5" index={4} />
        <StatCard label="Still owed" value={stats.deliverablesDue} icon={CalendarClock} accent="#e0433a" index={5} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <BentoCard title="Delivery" description="Delivered, out of what this month owes" className="lg:col-span-2" delay={0.05}>
          <div className="flex flex-col gap-4">
            {projects.filter((p) => p.status === 'Active').slice(0, 5).map((p) => {
              const client = clients.find((c) => c.id === p.clientId)
              const team = p.teamIds.map((id) => directory.find((m) => m.id === id)).filter(Boolean)
              return (
                <div key={p.id} className="flex items-center gap-4">
                  <div className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: client?.color }} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate text-sm font-medium text-canvas-fg">{p.name}</p>
                      <span className="text-xs font-medium text-canvas-fg/50 shrink-0">{formatDate(p.dueDate)}</span>
                    </div>
                    <div className="mt-1.5 flex items-center gap-3">
                      <ProgressBar value={p.progress} color={client?.color} height={5} trackClassName="bg-canvas-fg/15" />
                      <span className="w-9 shrink-0 text-right text-xs font-semibold text-canvas-fg/70">{p.progress}%</span>
                    </div>
                  </div>
                  <AvatarStack names={team.map((t) => t!.name)} colors={team.map((t) => t!.color)} max={3} />
                </div>
              )
            })}
          </div>
          {/* brand-500 clears 3.8:1 on both the light and the dark glass panel;
              the brand-300 this used to be only worked on the dark one. */}
          <Link to="/board" className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-brand-500 hover:text-brand-400">
            View all projects <ArrowRight size={14} />
          </Link>
        </BentoCard>

        <BentoCard title="Status" description="Every piece, by stage" delay={0.1}>
          <DonutChart
            data={taskStatusBreakdown}
            centerValue={`${tasks.length ? Math.round((stats.completedTasks / tasks.length) * 100) : 0}%`}
            centerLabel="Done"
          />
          <div className="mt-2 grid grid-cols-2 gap-2">
            {taskStatusBreakdown.map((s) => (
              <div key={s.name} className="flex items-center gap-1.5 text-xs text-canvas-fg/70">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                {s.name} <span className="font-semibold text-canvas-fg">{s.value}</span>
              </div>
            ))}
          </div>
        </BentoCard>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <BentoCard title="Due next" description="Latest first, worst first" delay={0.05}>
          <div className="flex flex-col gap-3">
            {upcoming.map((t) => {
              const due = daysUntil(t.dueDate)
              const client = clients.find((c) => c.id === t.clientId)
              return (
                <div key={t.id} className="flex items-center gap-3">
                  <button onClick={() => tick(t)} className="text-canvas-fg/30 hover:text-success-500">
                    <Circle size={16} />
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-canvas-fg">{t.title}</p>
                    <p className="truncate text-xs font-medium text-canvas-fg/50">{client?.name}</p>
                  </div>
                  <span className={`shrink-0 text-xs font-semibold ${due < 0 ? 'text-danger-500' : due <= 2 ? 'text-warning-500' : 'text-canvas-fg/50'}`}>
                    {due < 0 ? `${Math.abs(due)}d overdue` : due === 0 ? 'Today' : `${due}d left`}
                  </span>
                </div>
              )
            })}
          </div>
        </BentoCard>

        <BentoCard title="Activity" description="The studio and the agent, both" delay={0.1}>
          <div className="flex flex-col gap-4">
            {activityLog.slice(0, 5).map((a) => {
              const actor = directory.find((m) => m.id === a.actorId)
              return (
                <div key={a.id} className="flex items-start gap-3">
                  {actor && <Avatar name={actor.name} color={actor.color} size="xs" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-canvas-fg/70">
                      <span className="font-medium text-canvas-fg">{actor?.name}</span> {a.action}{' '}
                      <span className="font-medium text-canvas-fg">{a.target}</span>
                    </p>
                    <p className="mt-0.5 text-xs font-medium text-canvas-fg/50">{a.time}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </BentoCard>

        <BentoCard dark delay={0.15} className="flex flex-col justify-between">
          <div>
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 text-brand-400">
              <TrendingUp size={17} />
            </div>
            <h3 className="mt-4 font-display text-lg font-semibold text-white">Content Production</h3>
            <p className="mt-1 text-sm text-white/60">This month across all clients</p>
          </div>
          <div className="mt-5 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/60">Planned</span>
              <span className="font-semibold text-white">{contentStats.planned}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/60">Ready to go out</span>
              <span className="font-semibold text-white">{contentStats.completed}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/60">Posted</span>
              <span className="font-semibold text-white">{contentStats.posted}</span>
            </div>
            <ProgressBar
              value={contentStats.planned === 0 ? 0 : (contentStats.completed / contentStats.planned) * 100}
              color="#FC2265"
              trackClassName="bg-white/15"
            />
          </div>
          <Link to="/content" className="group mt-5 inline-flex items-center gap-2 text-sm font-medium text-white hover:text-white/80">
            Open content calendar
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-500 text-white transition-transform group-hover:translate-x-0.5">
              <ArrowRight size={12} />
            </span>
          </Link>
        </BentoCard>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <BentoCard title="Recent tasks" className="lg:col-span-2" delay={0.05}>
          <div className="divide-y divide-canvas-fg/10">
            {recentTasks.map((t) => {
              const assignee = directory.find((m) => m.id === t.assigneeId)
              return (
                <div key={t.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                  <button onClick={() => tick(t)} className="text-canvas-fg/30 hover:text-success-500">
                    {t.status === DONE_STATUS ? <CheckCircle2 size={16} className="text-success-500" /> : <Circle size={16} />}
                  </button>
                  <p className={`min-w-0 flex-1 truncate text-sm ${t.status === DONE_STATUS ? 'text-canvas-fg/35 line-through' : 'text-canvas-fg font-medium'}`}>
                    {t.title}
                  </p>
                  <StatusBadge status={t.status} />
                  {assignee && <Avatar name={assignee.name} color={assignee.color} size="xs" />}
                </div>
              )
            })}
          </div>
        </BentoCard>

        <div className="flex flex-col gap-4">
          <BentoCard title="Deliverables" description="Pieces posted, by the month they were owed for" delay={0.1}>
            {deliveredThisHalf === 0 ? (
              <p className="py-10 text-center text-sm text-canvas-fg/55">
                Nothing posted in the last six months yet. Pieces marked Posted show up here.
              </p>
            ) : (
              <BarChartMini data={monthlyDeliverables} height={160} />
            )}
          </BentoCard>

          <BentoCard
            title="Workload"
            description="Effort points committed, against capacity"
            delay={0.15}
            action={<Clock3 size={15} className="text-canvas-fg/50" />}
          >
            <div className="flex flex-col gap-3">
              {weeklyTime.map((entry) => {
                const member = directory.find((m) => m.id === entry.teamMemberId)
                if (!member) return null
                const pct = Math.round((entry.hoursLogged / entry.capacityHours) * 100)
                const overCapacity = entry.hoursLogged > entry.capacityHours
                return (
                  <div key={entry.teamMemberId} className="flex items-center gap-2.5">
                    <Avatar name={member.name} color={member.color} size="xs" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between">
                        <span className="truncate text-xs font-medium text-canvas-fg">{member.name.split(' ')[0]}</span>
                        <span className={cn('text-xs font-semibold tabular-nums', overCapacity ? 'text-danger-500' : 'text-canvas-fg/70')}>
                          {entry.hoursLogged}h
                        </span>
                      </div>
                      <ProgressBar
                        value={Math.min(pct, 100)}
                        color={overCapacity ? '#e0433a' : member.color}
                        height={4}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </BentoCard>
        </div>
      </div>

      {/*
        * Above the client list, not below it. A card that says somebody is
        * eleven days late is only useful before the morning gets going.
        */}
      <Chasing delay={0.05} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <BentoCard title="Clients" description="Who is behind, and by how much" className="lg:col-span-2" delay={0.05}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {clients.slice(0, 8).map((c) => (
            <motion.div whileHover={{ y: -2 }} key={c.id}>
              <Link
                to={`/clients/${c.id}`}
                className="flex items-center gap-3 rounded-2xl border border-canvas-fg/10 p-3 transition-colors hover:border-canvas-fg/25 hover:bg-canvas-fg/5"
              >
                <Avatar name={c.name} color={c.color} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-canvas-fg">{c.name}</p>
                  <p className="truncate text-xs font-medium text-canvas-fg/50">{c.activeProjects} active projects</p>
                </div>
                <StatusBadge status={c.accountStatus} />
              </Link>
            </motion.div>
          ))}
        </div>
      </BentoCard>

      <TimeLeaderboard delay={0.1} />
      </div>

      <NewTaskModal open={newTaskOpen} onClose={() => setNewTaskOpen(false)} />
    </div>
  )
}
